import { useCallback, useState } from "react";
import { IUserReport } from "../../helpers/Interfaces";
import { convertDuration, getPriority, validDateField, validStringField } from "../../helpers/HelperFunctions";
import SearchableDown from "../../components/DropDown/DropDown";
import RemoveFilter from "../../assets/images/RemoveFilter.svg";
import ArrowRightIcon from "../../assets/images/ArrowRight.svg";
import { UserReportFilterTypes } from "../../helpers/Types";
import AccordionOpen from "../../assets/images/AccordionOpenBlack.svg";

type UsersUiProps = {
    reports: IUserReport[];
    setSelectedReport: CallableFunction;
    openView: CallableFunction;
    filter: (filterBy: UserReportFilterTypes, query: string) => void;
    showFilter: boolean;
    cancelFilter: CallableFunction;
    searchReports: CallableFunction;
    selectedDateFilter: "createdDate" | "dueDate" | "submittedDate" | "completedDate";
    handleDateClick: (selectedDateFilter: "createdDate" | "dueDate" | "submittedDate" | "completedDate") => void;
    filterState: "ASCENDING" | "DESCENDING" | null;
}

export default function UsersUI(props: Readonly<UsersUiProps>) {

    const [selectedFilter, setSelectedFilter] = useState<UserReportFilterTypes>("patientName");

    function getInitialValues() {
        let valueArr = [] as any[];
        props.reports.forEach(report => {
            if (report[selectedFilter] != null) {
                valueArr.push(report[selectedFilter])
            }
            else {
                valueArr.push('-')
            }
        });

        let distinctValues = valueArr.filter((value, index, array) => array.indexOf(value) === index);
        return distinctValues;
    }

    const handleSelectedItem = (selectedValue: string) => {
        props.filter(selectedFilter, selectedValue);
    };

    const handleSearchOpen = (filterBy: UserReportFilterTypes) => {
        setSelectedFilter(filterBy);
    };

    const handleClick = useCallback((selectedReport: IUserReport) => {
        props.setSelectedReport(selectedReport);
        props.openView("view");
    }, []);

    return (<div className="table">
        <div className="header">
            <div className="header-row medium">
                <span className="text">Patient Name</span>
                <SearchableDown searchable={true} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={() => { handleSearchOpen("patientName") }} />
            </div>
            <div className="header-row medium">
                <span className="text">Doctor</span>
                <SearchableDown searchable={true} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={() => { handleSearchOpen("doctorName") }} />
            </div>
            <div className="header-row medium clickable" onClick={() => props.handleDateClick("createdDate")}>
                <span className="text">Created Date</span>
                {props.selectedDateFilter === "createdDate" && props.filterState !== null
                    ? <img src={AccordionOpen} alt="SortArrow" className={props.filterState === "ASCENDING" ? "image" : "invert-image"} />
                    : <span className="text image">-</span>
                }
            </div>
            <div className="header-row medium clickable" onClick={() => props.handleDateClick("submittedDate")}>
                <span className="text">Submitted Date</span>
                {props.selectedDateFilter === "submittedDate" && props.filterState !== null
                    ? <img src={AccordionOpen} alt="SortArrow" className={props.filterState === "ASCENDING" ? "image" : "invert-image"} />
                    : <span className="text image">-</span>
                }
            </div>
            <div className="header-row small">
                <span className="text">Duration</span>
            </div>
            <div className="header-row small">
                <span className="text">Scribed</span>
            </div>
            <div className="header-row small">
                <span className="text">Proofed</span>
            </div>
            <div className="header-row small">
                <span className="text">Reviewed</span>
            </div>
            <div className="header-row small">
                <span className="text">Urgency</span>
                <SearchableDown searchable={false} initialDetails={getInitialValues()}
                    onSelect={(selectedValue: string) => { handleSelectedItem(selectedValue); }}
                    setSelectedFilter={() => { handleSearchOpen("priority") }} />
            </div>
            <div className="header-row extra-small justify-end gap">
                {props.showFilter
                    ? <img className="filter-icon" src={RemoveFilter} alt={"RemoveFilter"} onClick={() => { props.cancelFilter(); }} />
                    : <div className="filter-icon" />
                }
            </div>
        </div>
        <div className="data">
            {props.reports.map((report) => {
                return (<div key={report.id} className="data-row pending-padding" onClick={() => { handleClick(report); }}>
                    <div className="cell text medium">{validStringField(report.patientName)}</div>
                    <div className="cell text medium">{validStringField(report.doctorName)}</div>
                    <div className="cell text medium"><span>{validDateField(report.createdDate)}</span></div>
                    <div className="cell text medium"><span>{validDateField(report.submittedDate)}</span></div>
                    <div className="cell text small">{convertDuration(report.totalDuration)}</div>
                    <div className="cell text small">{convertDuration(report.stageDetails?.scribe?.totalDuration)}</div>
                    <div className="cell text small">{convertDuration(report.stageDetails?.proof?.totalDuration)}</div>
                    <div className="cell text small">{convertDuration(report.stageDetails?.review?.totalDuration)}</div>
                    <div className="cell text small justify">{getPriority(report.priority)}</div>
                    <div className="cell text extra-small justify-end">
                        <img className="icon" src={ArrowRightIcon} alt={"ArrowRight"} />
                    </div>
                </div>)
            })}
        </div>
    </div>)
}